import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";

const MAX_POLLS = 12;         // 12 * 2s = 24s
const POLL_INTERVAL_MS = 2000;

export default function PaymentSuccess() {
  const [sp] = useSearchParams();
  const session_id = sp.get("session_id");
  const { refreshPurchases } = useApp();
  const [status, setStatus] = useState({ state: "polling", payment_status: "pending" });
  const attempts = useRef(0);
  const timer = useRef(null);

  useEffect(() => {
    if (!session_id) {
      setStatus({ state: "error", message: "Missing session reference." });
      return;
    }

    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${session_id}`);
        if (data.payment_status === "paid") {
          setStatus({ state: "paid", payment_status: "paid" });
          // Hydrate AppContext.purchases so My Temple shows the row immediately.
          try { await refreshPurchases(); } catch { /* silent */ }
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus({ state: "failed", payment_status: data.payment_status });
          return;
        }
        if (attempts.current >= MAX_POLLS) {
          setStatus({ state: "timeout", payment_status: data.payment_status });
          return;
        }
        timer.current = setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setStatus({ state: "error", message: "We couldn't confirm your payment. Please check My Temple." });
      }
    };
    poll();

    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [session_id]);

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
      <div className="rounded-2xl border p-8 text-center" style={{ background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" }} data-testid={`payment-success-state-${status.state}`}>
        {status.state === "polling" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto animate-spin mb-4" style={{ color: "var(--burgundy)" }} />
            <div className="eyebrow mb-2">Confirming your offering</div>
            <h2 className="font-serif text-2xl mb-2" style={{ color: "var(--burgundy)" }}>One moment, please.</h2>
            <p className="text-sm" style={{ color: "var(--text-muted)" }}>We are verifying your payment with Stripe. This usually takes a few seconds.</p>
          </>
        )}
        {status.state === "paid" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto mb-4" style={{ color: "var(--burgundy)" }} />
            <div className="eyebrow mb-2">Received with gratitude</div>
            <h2 className="font-serif text-3xl mb-3" style={{ color: "var(--burgundy)" }}>Thank you for your offering.</h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-obsidian)" }}>
              Your purchase is complete. You'll find your download in <strong>My Temple → Store Purchases</strong>.
            </p>
            <Link
              to="/my-temple"
              data-testid="payment-success-go-mytemple-btn"
              className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium"
              style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}
            >
              Open My Temple <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {(status.state === "failed" || status.state === "timeout" || status.state === "error") && (
          <>
            <AlertTriangle className="w-10 h-10 mx-auto mb-4" style={{ color: "var(--rose-dark)" }} />
            <div className="eyebrow mb-2">We couldn't confirm this</div>
            <h2 className="font-serif text-2xl mb-3" style={{ color: "var(--burgundy)" }}>
              {status.state === "failed" ? "Your payment did not go through." : "This is taking longer than usual."}
            </h2>
            <p className="text-sm mb-6" style={{ color: "var(--text-muted)" }}>
              {status.message || "If the charge is completed, it will still appear in My Temple once confirmed. You have not been double-charged."}
            </p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/store" className="rounded-full px-5 py-2.5 text-sm font-medium border" style={{ borderColor: "var(--burgundy)", color: "var(--burgundy)" }} data-testid="payment-back-to-store-link">
                Back to Store
              </Link>
              <Link to="/my-temple" className="rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }} data-testid="payment-open-mytemple-link">
                Check My Temple
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
